import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';
import CSSModule from 'react-css-modules';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';


// ASSUMPTION: same relative depth as Home.jsx's own imports of these two
// (components/CustomerHeader/ -> ../../{assets,constants}). Confirm/adjust
// if your actual folder depth differs.
import MyCouponLogo from '../../assets/images/MyCouponLogo.svg';
import MyCouponLogoMobile from '../../assets/images/MyCouponLogoMobile.svg';
import { RouteNames } from '../../constants';
import { LocationSearchModal } from '../../screens/Coupons/LocationSearch';
import styles from './CustomerHeader.module.scss';

const DEFAULT_CITY_NAME = 'Halifax';

/* ── Nav icons — simple line-art SVGs, same visual language as the
   reference pill-nav design (currentColor stroke, ~16-20px viewBox).
   Each icon accepts an `active` prop: when true it renders filled
   (solid), when false it renders as an outline (stroke only). ── */

const HomeIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9.15 1.86a1.35 1.35 0 0 1 1.7 0l6.53 5.28c.4.33.62.81.62 1.32v8.04c0 .94-.76 1.7-1.7 1.7h-4.15a.85.85 0 0 1-.85-.85v-4.5c0-.4-.05-.75-.13-.93a1.55 1.55 0 0 0-1.17-.94 1.9 1.9 0 0 0-.7 0 1.55 1.55 0 0 0-1.17.94c-.08.18-.13.53-.13.93v4.5c0 .47-.38.85-.85.85H3.3c-.94 0-1.7-.76-1.7-1.7V8.46c0-.51.22-.99.62-1.32l6.93-5.28Z"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.4}
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = ({ active }) => (
  <svg width="15" height="15" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle
      cx="8.5"
      cy="8.5"
      r="6.2"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.6}
    />
    <path d="M13.4 13.4 18 18" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BusinessesIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 8.2 3.6 2.6h12.8l1.1 5.6"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 8v8.6c0 .5.4.9.9.9h12.2c.5 0 .9-.4.9-.9V8"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 17.5V12h4v5.5"
      fill={active ? '#fff' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
  </svg>
);

const PartnersIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.4 11.2 3.3 8.1a1.4 1.4 0 0 1 0-2l2-2a1.4 1.4 0 0 1 2 0l3.1 3.1"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.6 8.8 16.7 11.9a1.4 1.4 0 0 1 0 2l-2 2a1.4 1.4 0 0 1-2 0l-3.1-3.1"
     fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
   <path
      d="m7.5 12.5 5-5"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 3.2 : 1.5}
      strokeLinecap="round"
    />
  </svg>
);

const MapsIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 18.2s6.3-5.6 6.3-10.4a6.3 6.3 0 1 0-12.6 0c0 4.8 6.3 10.4 6.3 10.4Z"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
    <circle
      cx="10"
      cy="7.8"
      r="2.3"
      fill={active ? '#fff' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
    />
  </svg>
);

const BlogIcon = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 4.2c0-.66.54-1.2 1.2-1.2h6.2c.66 0 1.2.54 1.2 1.2v11.6c0 .66-.54 1.2-1.2 1.2H4.2c-.66 0-1.2-.54-1.2-1.2V4.2Z"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
    <path
      d="M5.6 6.4h4.6M5.6 9h4.6M5.6 11.6h2.8"
      stroke={active ? '#fff' : 'currentColor'}
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M13.6 5.6 16.4 6.5c.62.2.96.87.76 1.49l-2.6 8c-.2.63-.87.97-1.49.77l-1.27-.41"
      fill={active ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={active ? 0 : 1.5}
      strokeLinejoin="round"
    />
  </svg>
);

const NAV_ITEMS = [
  { label: 'Home', to: RouteNames.HOME, Icon: HomeIcon },
  { label: 'Search', to: RouteNames.COUPONS_LISTS_ALL, Icon: SearchIcon },
  // { label: 'Businesses', to: RouteNames.BUSINESSES_LIST_ALL, Icon: BusinessesIcon },
  // { label: 'Maps', to: RouteNames.COUPONS_MAPS_ALL, Icon: MapsIcon },
  { label: 'Businesses', to: RouteNames.COUPONS_MAPS_ALL, Icon: BusinessesIcon },
  { label: 'Online Brands', to: RouteNames.AFFILIATE_BUSINESSES_LIST_ALL, Icon: PartnersIcon },
  { label: 'Blog', to: RouteNames.BLOGS, Icon: BlogIcon },
];

export const CustomerHeader = () => {
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  const { first_name: firstName, last_name: lastName } = useSelector(
    state => (state.customerProfile && state.customerProfile.data) || {},
  );

  const activeCityName = useSelector(state => (state.cities && state.cities.activeCity) || DEFAULT_CITY_NAME);

  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  const isActive = to => {
    const current = location.pathname.toLowerCase();
    const targets = [to.toLowerCase()];
    if (to === RouteNames.HOME && RouteNames.GUEST_HOME) {
      targets.push(RouteNames.GUEST_HOME.toLowerCase());
    }
    return targets.some(target => current === target || current.startsWith(`${target}/`));
  };
  // Routes with their own full-bleed hero image sit the nav on top of it
// (transparent/absolute) instead of the normal solid white bar. Add any
// future hero pages here.
const OVERLAY_ROUTES = [
  '/',
  RouteNames.HOME,
  RouteNames.GUEST_HOME,
  '/guest-home',
  RouteNames.COUPONS_LISTS_ALL,
  RouteNames.AFFILIATE_BUSINESSES_LIST_ALL,
];
// Blogs and Business Resources also overlay on their detail URLs
// (/blogs/:authorSlug/:blogSlug), which render the same hero page with the
// article modal on top.
const isBlogsRoute = isActive(RouteNames.BLOGS) || isActive('/business-resources');
const isHome =
  isBlogsRoute ||
  OVERLAY_ROUTES.filter(Boolean).some(route => location.pathname.toLowerCase() === route.toLowerCase());
  // Every page: same behaviour as the landing HeaderWrapper (About page) —
  // transparent at the top of the page, then a white blurred bar with a
  // soft shadow once scrolled. Over a dark hero (isHome) the transparent
  // state also uses white text + a soft top shade.
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  const atTop = !scrolled && !menuOpen;

  // The nav is position:fixed (html/body/#root are height:100%, so a sticky
  // wrapper only stays pinned for the first screen). Non-hero pages get a
  // spacer the height of the bar (not the open mobile menu) so their
  // content still starts below it.
  const navInnerRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);

  useEffect(() => {
    const el = navInnerRef.current;
    if (!el) return undefined;
    const measure = () => setNavHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const openLocationModal = () => setIsLocationModalOpen(true);
  const closeLocationModal = () => setIsLocationModalOpen(false);

  return (
    <>
            <nav
        styleName={cx('nav-root scroll-fade', {
          overlay: isHome,
          'scroll-fade-top': atTop && isHome,
        })}
      >
        <div styleName="nav-inner" ref={navInnerRef}>
          <Link to="/" styleName="nav-logo-wrap">
            <img src={MyCouponLogo} styleName="nav-logo-desktop" alt="MyCoupon" />
            <img src={MyCouponLogoMobile} styleName="nav-logo-mobile" alt="MyCoupon" />
          </Link>

          <div styleName="nav-right">
            {/* Desktop nav links — pill bar, hidden below 1200px via CSS */}
            <div styleName="navbar-links nav-desktop-only">
              {NAV_ITEMS.map(({ label, to, Icon }) => (
                <Link
                  key={label}
                  to={to}
                  className={cx(styles['navbar-link'], { [styles['is-active']]: isActive(to) })}
                  aria-label={label}
                  title={label}
                >
                  <span styleName="navbar-link-icon">
                    <Icon active={isActive(to)} />
                  </span>
                  <span styleName="navbar-link-text">{label}</span>
                </Link>
              ))}
            </div>

         <button type="button" styleName="location-btn" onClick={openLocationModal}>
  <svg styleName="location-icon" viewBox="0 0 24 24" fill="#E00000" aria-hidden="true">
    <path d="M12 2C7.58 2 4 5.58 4 10c0 5.25 7 12 7.41 12.41a.83.83 0 0 0 1.18 0C13 22 20 15.25 20 10c0-4.42-3.58-8-8-8Zm0 11a3 3 0 1 1 0-6 3 3 0 0 1 0 6Z" />
  </svg>
  {activeCityName}
</button>

{!fullName && (
  <button
    type="button"
    styleName="name-btn signin-btn nav-desktop-only"
    onClick={() => navigate('/business')}
  >
    Join as a Business
  </button>
)}

{fullName ? (
  <button
    type="button"
    styleName="name-btn nav-desktop-only"
    onClick={() => navigate(RouteNames.ACCOUNT)}
  >
    {fullName}
  </button>
) : (
  <button
    type="button"
    styleName="join-business-btn nav-desktop-only"
    onClick={() => navigate(RouteNames.SIGN_IN)}
  >
    Sign In
  </button>
)}
            {/* Hamburger — visible only on small/tablet via CSS */}
            <button
              type="button"
              styleName="nav-ham nav-mobile-only"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>

        {/* Mobile/tablet dropdown — toggled by JS state */}
        {menuOpen && (
          <div styleName="nav-mobile-menu">
            {NAV_ITEMS.map(({ label, to, Icon }) => (
              <Link
                key={label}
                to={to}
                className={cx(styles['navbar-mobile-link'], { [styles['is-active']]: isActive(to) })}
                onClick={() => setMenuOpen(false)}
              >
                <span styleName="navbar-link-icon">
                  <Icon active={isActive(to)} />
                </span>
                <span>{label}</span>
              </Link>
            ))}

            {fullName ? (
              <button
                type="button"
                styleName="navbar-mobile-link"
                onClick={() => {
                  setMenuOpen(false);
                  navigate(RouteNames.ACCOUNT);
                }}
              >
                <span>{fullName}</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  styleName="navbar-mobile-link"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/business');
                  }}
                >
                  <span>Join as a Business</span>
                </button>
                <button
                  type="button"
                  styleName="navbar-mobile-link signin-btn"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate(RouteNames.SIGN_IN);
                  }}
                >
                  <span>Sign In</span>
                </button>
              </>
            )}
          </div>
        )}
      </nav>
      {!isHome && <div aria-hidden="true" style={{ height: navHeight }} />}

      <LocationSearchModal isOpen={isLocationModalOpen} onClose={closeLocationModal} />
    </>
  );
};

export default CSSModule(CustomerHeader, styles, { allowMultiple: true });